import { useState } from "react";
import { useIsCompact } from "./useIsCompact";
import { FullscreenModal } from "./FullscreenModal";
import { SignaturePreview } from "./SignaturePreview";
import { SignaturePadEditable } from "./SignaturePadEditable";
import type { SignatureStroke } from "./SignatureTypes";
import { Label } from "../label";

type SignatureFieldProps = {
  label?: string;
  valueStrokes?: SignatureStroke[];
  valuePng?: string;
  onChange?: (strokes: SignatureStroke[], png: string) => void;
};

export function SignatureField({
  label = "Handtekening",
  valueStrokes,
  valuePng,
  onChange,
}: SignatureFieldProps) {
  const isCompact = useIsCompact(768);

  const [strokes, setStrokes] = useState<SignatureStroke[]>(valueStrokes || []);
  const [png, setPng] = useState<string>(valuePng || "");
  const [open, setOpen] = useState(false);

  const [version, setVersion] = useState(0);

  function commitSignature(
    nextStrokes: SignatureStroke[],
    nextPng: string,
    bump?: boolean
  ) {
    setStrokes(nextStrokes);
    setPng(nextPng);
    onChange?.(nextStrokes, nextPng);

    if (bump) {
      setVersion((v) => v + 1);
    }
  }

  return (
    <div className="space-y-2 relative">
      <Label className="text-sm font-medium text-neutral-600">
        {label}
      </Label>
      {isCompact ? (
        <div className="flex flex-col gap-2">
          <SignaturePreview
            png={png}
            onClick={() => setOpen(true)}
          />

          <button
            type="button"
            className="text-sm px-2 py-1 rounded border border-neutral-300 text-neutral-700 bg-white font-medium inline-flex justify-center"
            onClick={() => setOpen(true)}
          >
            Ondertekenen / Bewerken
          </button>
        </div>
      ) : (
        <div className="w-full rounded border border-neutral-300 bg-white shadow-inner">
          <SignaturePadEditable
            key={version}
            mode="inline"
            initialStrokes={strokes}
            showConfirmRow={false}
            onChange={(newStrokes, newPng) => {
              commitSignature(newStrokes, newPng, false);
            }}
          />
        </div>
      )}

      {open && (
        <FullscreenModal
          onClose={() => {
            setOpen(false);
          }}
        >
          <SignaturePadEditable
            mode="fullscreen"
            initialStrokes={strokes}
            showConfirmRow={true}
            onDone={(finalStrokes, finalPng) => {
              commitSignature(finalStrokes, finalPng, true);
              setOpen(false);
            }}
            onCancel={() => {
              setOpen(false);
            }}
          />
        </FullscreenModal>
      )}
    </div>
  );
}
